// The join between a material_mapping row and the three.js object it dresses.
//
// The server writes one row per glTF PRIMITIVE, keyed (meshName, primitiveIndex)
// where meshName is the glTF NODE's name (else the mesh's, else `mesh_<i>`) —
// glb_import/service.go, step 4. The runtime used to join by the NAME three.js
// gave the object, which is a different thing: three names primitive 0 after the
// node and suffixes the rest through one global counter (`Face`, `Face_1`, ...,
// shared with lights, cameras and every other named thing), so eight of a
// MetaHuman face's nine primitives could never find their row and wore the
// placeholder. Names were a proxy for provenance; GLTFLoader keeps the
// provenance itself in `parser.associations` (object -> { meshes, primitives,
// nodes }), and that is what this module reads. The result is stamped on
// `object.userData.materialSlot` once, at load, so the binding walk stays a
// lookup and needs no parser.
//
// Name-based binding remains the fallback for scenes that did not come through
// GLTFLoader (tests, procedural meshes, a rig assembled by hand): an object with
// no stamp binds by name exactly as before.

export const SLOT_KEY_SEP = '#'

/** The lookup key a mapping row and a stamped object agree on. */
export function slotKey(meshName, primitiveIndex) {
  return `${meshName}${SLOT_KEY_SEP}${Number(primitiveIndex ?? 0)}`
}

/** How a slot reads in a report or a log line: `Face[3]`; primitive 0 stays bare. */
export function slotLabel(meshName, primitiveIndex) {
  const pi = Number(primitiveIndex ?? 0)
  return pi === 0 ? String(meshName) : `${meshName}[${pi}]`
}

/**
 * Pure: given a mesh object, the loader's associations Map and the glTF JSON,
 * returns `{ meshName, primitiveIndex, nodeIndex, meshIndex }` or null when the
 * object did not come from a glTF mesh primitive.
 *
 * The node is found by walking up from the object to the nearest ancestor-or-
 * self whose association carries `nodes` — for a single-primitive mesh that is
 * the object itself, for a multi-primitive mesh it is the Group the loader put
 * the primitives under. The name is the JSON's, never the object's: the object's
 * was uniquified by three and is exactly the thing this module stops trusting.
 */
export function resolveGltfSlot(object, associations, json) {
  const assoc = associations?.get?.(object)
  if (!assoc || assoc.meshes === undefined) return null
  const meshIndex = assoc.meshes
  const primitiveIndex = assoc.primitives ?? 0
  let nodeIndex
  for (let o = object; o; o = o.parent) {
    const a = associations.get(o)
    if (a && a.nodes !== undefined) { nodeIndex = a.nodes; break }
  }
  const nodeDef = nodeIndex !== undefined ? json?.nodes?.[nodeIndex] : undefined
  const meshDef = json?.meshes?.[meshIndex]
  const meshName = (nodeDef?.name ?? '') || (meshDef?.name ?? '') || `mesh_${meshIndex}`
  return { meshName, primitiveIndex, nodeIndex, meshIndex }
}

/**
 * Stamps `userData.materialSlot = { meshName, primitiveIndex }` on every mesh
 * under root that GLTFLoader can account for. Returns how many were stamped.
 * Idempotent; a mesh with no association is left untouched (it will bind by name).
 */
export function annotateGltfSlots(root, parser) {
  if (!root || !parser?.associations) return 0
  let n = 0
  root.traverse((o) => {
    // A LINES primitive (hair strands, hairStrands.js) is a slot as much as a
    // triangle primitive: the server's mapping has a row for it.
    if (!o.isMesh && !o.isLine) return
    const slot = resolveGltfSlot(o, parser.associations, parser.json)
    if (!slot) return
    o.userData.materialSlot = { meshName: slot.meshName, primitiveIndex: slot.primitiveIndex }
    n++
  })
  return n
}

/** The stamp, if any, as a lookup key. */
export function slotKeyOf(object) {
  const s = object?.userData?.materialSlot
  return s ? slotKey(s.meshName, s.primitiveIndex) : null
}
